import {useEffect, useRef, useState} from 'react';

import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import {colors} from 'shells/colors/colors';
import {LocationPinSolidIcon} from 'shells/icon/svg/location-pin-solid-icon';
import {Link} from 'shells/link/link';

import type {PeepType} from '../../../types';

import {MAPLIBRE_ZOOM_LEVELS, MAPLIBRE_ZOOM_TRANSITION_MS, MAP_DATA_URL} from './consts';
import {getTargetZoom} from './helpers';

import styles from './maplibre-map-layer.module.css';

interface Props {
    peepType: PeepType;
    latitude: number;
    longitude: number;
    locationAccuracy: number;
    isHovered: boolean;
    isSuperZoom: boolean;
    showBaseShadow?: boolean;
    onWebGLError?: () => void;
}

export function MaplibreMapLayer(props: Props) {
    const {onWebGLError} = props;

    const [isReady, setIsReady] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<maplibregl.Map | null>(null);

    const markerColor = props.peepType === 'account' ? colors.company : colors.blue;

    useEffect(() => {
        if (!containerRef.current) return;

        setIsReady(false);

        let map: maplibregl.Map;
        try {
            map = new maplibregl.Map({
                container: containerRef.current,
                style: MAP_DATA_URL,
                center: [props.longitude, props.latitude],
                zoom: MAPLIBRE_ZOOM_LEVELS.base,
                interactive: false,
                attributionControl: false,
            });
        } catch {
            onWebGLError?.();
            return;
        }

        map.once('idle', () => setIsReady(true));
        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, [props.latitude, props.longitude, onWebGLError]);

    // Handle zoom transition on hover/superzoom
    useEffect(() => {
        if (!isReady || !mapRef.current) return;

        mapRef.current.easeTo({
            zoom: getTargetZoom(props.isHovered, props.isSuperZoom, props.locationAccuracy),
            duration: MAPLIBRE_ZOOM_TRANSITION_MS,
        });
    }, [isReady, props.isHovered, props.isSuperZoom, props.locationAccuracy]);

    return (
        <div className={styles['container']}>
            {props.showBaseShadow && <div className={styles['shadow-inset']} />}
            <div ref={containerRef} className={styles['map-canvas']} />
            <Link
                className={styles['attribution']}
                href='https://www.openstreetmap.org/copyright'
                newTab={true}
                size='mini'
            >
                © OpenStreetMap
            </Link>
            <div className={styles['marker-icon']}>
                <LocationPinSolidIcon
                    size={48}
                    // @ts-expect-error
                    color={markerColor}
                    wrapWithDiv={false}
                />
            </div>
        </div>
    );
}
